import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'

import { useMutation } from '@tanstack/react-query'
import { getRouteApi, useRouter } from '@tanstack/react-router'
import { Request } from 'commons/utils/request'
import { Toast } from 'ui/components'
import { AdverseMediaDetailsType } from '~bank-adverse-media/types/details'

import { handlerServerError } from '@orbit/errors'

import { SelectionPublicationContext } from './SelectionPublicationContext'

type SelectionProviderProps = {
  children: ReactNode
}

export const SelectionPublicationContextProvider: FC<SelectionProviderProps> = ({
  children
}: SelectionProviderProps) => {
  const router = useRouter()
  const routeApi = getRouteApi('/_app/adverse-media/$id')
  const loaderData = routeApi.useLoaderData({ select: (s) => s }) as AdverseMediaDetailsType
  const allUnreviewedPublications = loaderData.publications.filter((p) => p.needsReview)

  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [isAllSelected, setIsAllSelected] = useState(false)

  const toggleId = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const index = prev.findIndex((itemId) => itemId === id)
      if (index === -1) {
        return [...prev, id]
      } else {
        const copy = [...prev]
        copy.splice(index, 1)
        return copy
      }
    })
  }, [])

  const selectAll = () => {
    const newIsAllSelected = !isAllSelected
    setIsAllSelected(newIsAllSelected)

    if (newIsAllSelected) {
      const allIds = allUnreviewedPublications.map((pub) => pub.id)
      setSelectedIds(allIds)
    } else {
      setSelectedIds([])
    }
  }

  useEffect(() => {
    if (allUnreviewedPublications.length === 0) {
      setIsAllSelected(false)
      return
    }

    const allIds = allUnreviewedPublications.map((pub) => pub.id)
    const allSelected = allIds.every((id) => selectedIds.includes(id))
    setIsAllSelected(allSelected)
  }, [allUnreviewedPublications])

  const clearSelection = useCallback(() => {
    setSelectedIds([])
  }, [])

  const hasSelectedItems = useMemo(() => selectedIds.length > 0, [selectedIds])

  const { mutate: deletePublications } = useMutation<void, Error, { id: number }>({
    mutationFn: (opts) => Request.put(Request.urls.bank.adverseMediaCaseClearUnreviewedPublications, opts),
    onSuccess: async () => {
      clearSelection()
      Toast.successSmall('All unreviewed publications have been deleted')
      await router.invalidate()
    },
    onError: (e) => handlerServerError(e)
  })

  const deleteAllUnreviewed = () => deletePublications({ id: loaderData.id })

  const isItemSelected = useCallback(
    (id: number) => {
      return selectedIds.includes(id)
    },
    [selectedIds]
  )

  const value = {
    selectedIds,
    toggleId,
    selectAll,
    clearSelection,
    hasSelectedItems,
    deleteAllUnreviewed,
    isItemSelected,
    isAllSelected
  }

  return <SelectionPublicationContext.Provider value={value}>{children}</SelectionPublicationContext.Provider>
}
